/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description clearing house constants
 */

import _ from 'lodash';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';

// clearing house types
const CLEARING_HOUSE_TYPE: Record<string, ClearingHouse.ClearingHouseType> = {
	availity: 'Availity',
	stedi: 'Stedi',
	changeHealthcare: 'Change Healthcare',
};

// clearing house type options
const CLEARING_HOUSE_TYPE_OPTIONS = _.map(CLEARING_HOUSE_TYPE, (value) => ({
	value,
	label: value,
}));

// clearing house transaction types
const CLEARING_HOUSE_TRANSACTION_TYPE: Record<string, { value: ClearingHouse.TransactionType; label: string; tagColor: string }> = {
	payment: { value: '835', label: '835', tagColor: ANT_TAG_COLORS.red },
	claim: { value: '837', label: '837', tagColor: ANT_TAG_COLORS.purple },
	acknowledgment: { value: '999', label: '999', tagColor: ANT_TAG_COLORS.blue },
	claimAck: { value: '277CA', label: '277CA', tagColor: ANT_TAG_COLORS.orange },
};

// clearing house transaction type options
const CLEARING_HOUSE_TRANSACTION_TYPE_OPTIONS = _.map(CLEARING_HOUSE_TRANSACTION_TYPE, (value) => ({
	value: value?.value,
	label: value?.label,
}));

// clearing house status
const CLEARING_HOUSE_STATUS: Record<string, { value: ClearingHouse.ClearingHouseStatus; label: string; tagColor: string }> = {
	active: { value: 1, label: 'Active', tagColor: ANT_TAG_COLORS.green },
	inactive: { value: 0, label: 'Inactive', tagColor: ANT_TAG_COLORS.default },
	deleted: { value: -1, label: 'Deleted', tagColor: ANT_TAG_COLORS.red },
};

// clearing house modes
const CLEARING_HOUSE_MODE: Record<string, { value: ClearingHouse.ClearingHouseMode; label: string }> = {
	sftp: { value: 'sftp', label: 'SFTP Based' },
	api: { value: 'api', label: 'API' },
	webhook: { value: 'webhook', label: 'Webhook Based' },
};

// clearing house mode options
const CLEARING_HOUSE_MODE_OPTIONS = _.map(CLEARING_HOUSE_MODE, (value) => ({
	value: value?.value,
	label: value?.label,
}));

// clearing house schedule types
const CLEARING_HOUSE_SCHEDULE_TYPE: Record<ClearingHouse.ScheduleType, { value: ClearingHouse.ScheduleType; label: string }> = {
	interval: { value: 'interval', label: 'Periodic' },
	calendar: { value: 'calendar', label: 'Calendar' },
};

// clearing house schedule type options
const CLEARING_HOUSE_SCHEDULE_TYPE_OPTIONS = _.values(CLEARING_HOUSE_SCHEDULE_TYPE);

// its time delta in ISO 8601 format
const CLEARING_HOUSE_FREQUENCY_INTERVAL_OPTIONS: { label: string; value: string }[] = [
	{ label: 'Every 15 minutes', value: 'PT15M' },
	{ label: 'Every 30 minutes', value: 'PT30M' },
	{ label: 'Every 1 hour', value: 'PT1H' },
	{ label: 'Every 2 hours', value: 'PT2H' },
	{ label: 'Every 6 hours', value: 'PT6H' },
	{ label: 'Every 12 hours', value: 'PT12H' },
	{ label: 'Every day', value: 'P1D' },
];

// clearing house schedule day options
const CLEARING_HOUSE_SCHEDULE_DAY_OPTIONS: { label: string; value: number }[] = [
	{ label: 'Monday', value: 1 },
	{ label: 'Tuesday', value: 2 },
	{ label: 'Wednesday', value: 3 },
	{ label: 'Thursday', value: 4 },
	{ label: 'Friday', value: 5 },
	{ label: 'Saturday', value: 6 },
	{ label: 'Sunday', value: 7 },
];

// clearing house form fields
const FORM_FIELDS: Partial<
	Record<keyof ClearingHouse.IFormModel, { title: string; placeholder: string; errorMessage?: string; validationMessage?: string }>
> = {
	vendor: { title: 'Vendor Name', placeholder: 'Select Vendor', errorMessage: 'Please select the vendor name' },
	nickname: { title: 'Display Name', placeholder: 'Enter Unique Display Name', errorMessage: 'Please enter the display name' },
	status: { title: 'Status', placeholder: 'Select Status', errorMessage: 'Please select the status' },
	transactionType: {
		title: 'Transaction Type',
		placeholder: 'Select Transaction Type',
		errorMessage: 'Please select the transaction type',
	},

	// 837 (Claim)
	mode837: { title: 'Mode Selection', placeholder: 'Please select the Mode', errorMessage: 'Please select the mode' },
	senderId: { title: 'Sender ID', placeholder: 'E.g.:  ABC12345', errorMessage: 'Please enter the sender id' },
	receiverId: { title: 'Receiver ID', placeholder: 'E.g.:  XYZ67890', errorMessage: 'Please enter the receiver id' },
	senderCode: { title: 'Sender Code', placeholder: 'E.g.:  ABC12345', errorMessage: 'Please enter the sender code' },
	status837: { title: 'Status', placeholder: 'Select Status', errorMessage: 'Please select the status' },

	// 837 SFTP
	sftpHost837: { title: 'SFTP Host', placeholder: 'Enter SFTP URL', errorMessage: 'Please enter the sftp host' },
	sftpPort837: { title: 'SFTP Port', placeholder: 'E.g.: 22', errorMessage: 'Please enter the sftp port' },
	username837: { title: 'Username', placeholder: 'Enter SFTP username', errorMessage: 'Please enter the SFTP username' },
	password837: { title: 'Password', placeholder: 'Enter SFTP password', errorMessage: 'Please enter the SFTP password' },

	// 837 API
	apiClientId837: { title: 'API Client ID', placeholder: 'Enter Client ID', errorMessage: 'Please enter the client id' },
	apiClientSecret837: { title: 'API Client Secret', placeholder: 'Enter Client Secret', errorMessage: 'Please enter the client secret' },
	apiBaseUrl837: { title: 'API Base URL', placeholder: 'Enter URL', errorMessage: 'Please enter the API base url' },

	// 837 Webhook
	webhookApiKey837: { title: 'API Key', placeholder: 'Enter API Key', errorMessage: 'Please enter the API key' },
	webhookApiBaseUrl837: {
		title: 'API Base URL',
		placeholder: 'Enter URL',
		errorMessage: 'Please enter the API base url',
	},

	// 999 (Acknowledgment)
	mode999: { title: 'Mode Selection', placeholder: 'Please select the Mode', errorMessage: 'Please select the mode' },
	status999: { title: 'Status', placeholder: 'Select Status', errorMessage: 'Please select the status' },

	// 999 SFTP
	sftpHost999: { title: 'SFTP Host', placeholder: 'Enter SFTP URL', errorMessage: 'Please enter the sftp host' },
	sftpPort999: { title: 'SFTP Port', placeholder: 'E.g.: 22', errorMessage: 'Please enter the sftp port' },
	username999: { title: 'Username', placeholder: 'Enter SFTP username', errorMessage: 'Please enter the SFTP username' },
	password999: { title: 'Password', placeholder: 'Enter SFTP password', errorMessage: 'Please enter the SFTP password' },

	// 999 API
	apiClientId999: { title: 'API Client ID', placeholder: 'Enter Client ID', errorMessage: 'Please enter the client id' },
	apiClientSecret999: { title: 'API Client Secret', placeholder: 'Enter Client Secret', errorMessage: 'Please enter the client secret' },
	apiBaseUrl999: { title: 'API Base URL', placeholder: 'Enter URL', errorMessage: 'Please enter the API base url' },

	// 999 Webhook
	webhookApiKey999: { title: 'API Key', placeholder: 'Enter API Key', errorMessage: 'Please enter the API key' },
	webhookApiBaseUrl999: { title: 'API Base URL', placeholder: 'Enter URL', errorMessage: 'Please enter the API base url' },

	// 277CA (Claim Acknowledgment)
	mode277CA: { title: 'Mode Selection', placeholder: 'Please select the Mode', errorMessage: 'Please select the mode' },
	status277CA: { title: 'Status', placeholder: 'Select Status', errorMessage: 'Please select the status' },

	// 277CA SFTP
	sftpHost277CA: { title: 'SFTP Host', placeholder: 'Enter SFTP URL', errorMessage: 'Please enter the sftp host' },
	sftpPort277CA: { title: 'SFTP Port', placeholder: 'E.g.: 22', errorMessage: 'Please enter the sftp port' },
	username277CA: { title: 'Username', placeholder: 'Enter SFTP username', errorMessage: 'Please enter the SFTP username' },
	password277CA: { title: 'Password', placeholder: 'Enter SFTP password', errorMessage: 'Please enter the SFTP password' },

	// 277CA API
	apiClientId277CA: { title: 'API Client ID', placeholder: 'Enter Client ID', errorMessage: 'Please enter the client id' },
	apiClientSecret277CA: {
		title: 'API Client Secret',
		placeholder: 'Enter Client Secret',
		errorMessage: 'Please enter the client secret',
	},
	apiBaseUrl277CA: { title: 'API Base URL', placeholder: 'Enter URL', errorMessage: 'Please enter the API base url' },

	// 277CA Webhook
	webhookApiKey277CA: { title: 'API Key', placeholder: 'Enter API Key', errorMessage: 'Please enter the API key' },
	webhookApiBaseUrl277CA: { title: 'API Base URL', placeholder: 'Enter URL', errorMessage: 'Please enter the API base url' },

	// Schedule/Fetch (835)
	lastSyncTime835: { title: 'Last Syncup Timestamp', placeholder: 'yyyy-mm-dd HH:mm:ss TZ' },
	fetchMode835: { title: 'Fetch Mode', placeholder: 'E.g.: Periodic', errorMessage: 'Please select the fetch mode' },
	fetchInterval835: {
		title: 'Frequency Interval',
		placeholder: 'E.g.: Every 15 minutes',
		errorMessage: 'Please select the frequency interval',
	},
	scheduleDay835: { title: 'Scheduled Days', placeholder: 'E.g.: Monday', errorMessage: 'Please select the schedule day(s)' },
	fetchTime835: { title: 'Fetch Time', placeholder: '14:00:00', errorMessage: 'Please select the fetch time' },
	timezone835: { title: 'Timezone', placeholder: 'E.g.: America/Chicago ( -05:00 )', errorMessage: 'Please select the timezone' },

	// Schedule/Fetch (999)
	lastSyncTime999: { title: 'Last Syncup Timestamp', placeholder: 'yyyy-mm-dd HH:mm:ss TZ' },
	fetchMode999: { title: 'Fetch Mode', placeholder: 'E.g.: Periodic', errorMessage: 'Please select the fetch mode' },
	fetchInterval999: {
		title: 'Frequency Interval',
		placeholder: 'E.g.: Every 15 minutes',
		errorMessage: 'Please select the frequency interval',
	},
	scheduleDay999: { title: 'Scheduled Days', placeholder: 'E.g.: Monday', errorMessage: 'Please select the schedule day(s)' },
	fetchTime999: { title: 'Fetch Time', placeholder: '14:00:00', errorMessage: 'Please select the fetch time' },
	timezone999: { title: 'Timezone', placeholder: 'E.g.: America/Chicago ( -05:00 )', errorMessage: 'Please select the timezone' },

	// Schedule/Fetch (277CA)
	lastSyncTime277CA: { title: 'Last Syncup Timestamp', placeholder: 'yyyy-mm-dd HH:mm:ss TZ' },
	fetchMode277CA: { title: 'Fetch Mode', placeholder: 'E.g.: Periodic', errorMessage: 'Please select the fetch mode' },
	fetchInterval277CA: {
		title: 'Frequency Interval',
		placeholder: 'E.g.: Every 15 minutes',
		errorMessage: 'Please select the frequency interval',
	},
	scheduleDay277CA: { title: 'Scheduled Days', placeholder: 'E.g.: Monday', errorMessage: 'Please select the schedule day(s)' },
	fetchTime277CA: { title: 'Fetch Time', placeholder: '14:00:00', errorMessage: 'Please select the fetch time' },
	timezone277CA: { title: 'Timezone', placeholder: 'E.g.: America/Chicago ( -05:00 )', errorMessage: 'Please select the timezone' },
};

export {
	CLEARING_HOUSE_TYPE,
	CLEARING_HOUSE_TYPE_OPTIONS,
	CLEARING_HOUSE_TRANSACTION_TYPE,
	CLEARING_HOUSE_TRANSACTION_TYPE_OPTIONS,
	CLEARING_HOUSE_STATUS,
	CLEARING_HOUSE_MODE,
	CLEARING_HOUSE_MODE_OPTIONS,
	CLEARING_HOUSE_SCHEDULE_TYPE,
	CLEARING_HOUSE_SCHEDULE_TYPE_OPTIONS,
	CLEARING_HOUSE_FREQUENCY_INTERVAL_OPTIONS,
	CLEARING_HOUSE_SCHEDULE_DAY_OPTIONS,
	FORM_FIELDS,
};
